/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { FC, forwardRef, useEffect, useState } from 'react';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  Col,
  Collapse,
  Row,
  Spin,
  Tabs,
  Menu,
  Dropdown,
  message,
  Tooltip,
  Button,
} from 'antd';
import { useIntl } from 'react-intl';
import { useQuery, useMutation, queryCache } from 'react-query';
import {
  getContractEvaluationOffers,
  getContractScreeningOffers,
} from '~src/services/api/offers';
import { OfferItem } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OfferItem';
import { getOfferStatusVisualIntl } from '~src/utils/shareUtils/useShareKeysStatusUtils';
import {
  getContracts,
  setContractWinner,
  removeContractWinner,
} from '~src/services/api/contracts';
import { OffersLeftPanel } from './OffersLeftPanel';
import { OffersRightCore } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OfferRightPanelCore';
import { OffersScreeningContractView } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersScreening/OffersScreeningContractView';
import { TenderRegistrationVM } from '~src/types/db';
import IconButton from '~components/_common/IconButton/IconButton';
import Icon from '~components/_common/AppIcon/Icon';
import dataContextMenu from '~src/data/dataContextMenu';
import { OffersEvaluationContractView } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersEvaluation/OffersEvaluationContractView';
import { OffersResultsContractView } from '~components/molecules/Tabs/TenderTabs/components/OffersNew/OffersResults/OffersResultsContractView';
import { OffersMessagesModal } from '~components/popups-modals/OffersTabModals/OffersMessagesModal';

const { Panel } = Collapse;
const { TabPane } = Tabs;

type OffersTabNewProps = {
  projectId: number;
  tenderId: number;
  tenderRegData?: TenderRegistrationVM | undefined;
};

export const OffersTabNew = forwardRef<HTMLDivElement, OffersTabNewProps>(
  ({ projectId, tenderId, tenderRegData }, ref) => {
    const intl = useIntl();
    const styles = useStyles(styleFn);

    const tenderClosed = tenderRegData?.state === 6;
    // console.log(`tenderRegData`, tenderRegData);

    const { data: contracts = [], isLoading: isContractsLoading } = useQuery(
      ['contracts', projectId, tenderId],
      () => getContracts({ projectId: projectId!, tenderId: tenderId! }),
      { enabled: !!projectId && !!tenderId }
    );

    const [doSetWinner] = useMutation(setContractWinner, {
      onSuccess: () => {
        queryCache.invalidateQueries(['contracts', projectId, tenderId]);
        message.success(intl.formatMessage({ id: 'winner-set-success' }));
      },
    });

    const [doRemoveWinner] = useMutation(removeContractWinner, {
      onSuccess: () => {
        queryCache.invalidateQueries(['contracts', projectId, tenderId]);
        message.success(intl.formatMessage({ id: 'winner-removed-success' }));
      },
    });

    // useQuery(
    //   ['contracts-offers', projectId, tenderId],
    //   () =>
    //     getAllOffersContracts({ projectId: projectId!, tenderId: tenderId! }),
    //   { enabled: !!projectId && !!tenderId }
    // );

    const [activeContractKey, setActiveContractKey] = useState<
      string | undefined
    >(undefined);

    const [offerState, setOfferState] = useState<1 | 2>(1);

    useEffect(() => {
      if (contracts && contracts.length && !activeContractKey) {
        setActiveContractKey(String(contracts[0].id));
      }
    }, [contracts, activeContractKey]);

    if (isContractsLoading) return <Spin />;

    return (
      <div css={styles.container}>
        <Row gutter={24} wrap>
          {/* Left column – visible only in state 1 */}
          {!tenderClosed && (
            <Col xs={24} lg={12} css={styles.leftCol}>
              <OffersLeftPanel
                offerState={offerState}
                onOfferStateChange={setOfferState}
                tenderRegData={tenderRegData}
                projectId={projectId}
                tenderId={tenderId}
              />
            </Col>
          )}

          {/* Right column – full width in state 2 */}
          <Col xs={24} lg={!tenderClosed ? 12 : 24} css={styles.rightCol}>
            {!tenderClosed ? (
              <div css={styles.rightHeaderHint}>
                {intl.formatMessage({
                  id: 'offers-core-title',
                })}
              </div>
            ) : (
              <div css={styles.summaryHeaderBlock}>
                <div css={styles.summaryHeaderTitle}>
                  {intl.formatMessage({ id: 'offers-summary-title' })}
                </div>

                <div css={styles.summaryHeaderText}>
                  {intl.formatMessage({ id: 'offers-summary-description' })}
                </div>
              </div>
            )}

            {!tenderClosed ? (
              // STATE 1 — “core” overview
              <div css={styles.tabPaneBody}>
                <div css={styles.tabContentScroll}>
                  <OffersRightCore projectId={projectId} tenderId={tenderId} />
                </div>
              </div>
            ) : contracts.length === 1 ? (
              // Single contract - no tabs needed
              <div css={styles.tabPaneBody}>
                <OffersContractProcessContent
                  projectId={projectId}
                  tenderId={tenderId}
                  contract={contracts[0]}
                  doSetWinner={doSetWinner}
                  doRemoveWinner={doRemoveWinner}
                />
              </div>
            ) : (
              // Multiple contracts - show tabs
              <div css={styles.tabsWrap}>
                <Tabs
                  activeKey={activeContractKey}
                  onChange={setActiveContractKey}
                  type="line"
                  tabBarGutter={12}
                  animated={false}
                  destroyInactiveTabPane={false}
                  style={{ width: '100%' }}>
                  {contracts.map((contract) => (
                    <TabPane tab={contract.name} key={String(contract.id)}>
                      <OffersContractProcessContent
                        projectId={projectId}
                        tenderId={tenderId}
                        contract={contract}
                        doSetWinner={doSetWinner}
                        doRemoveWinner={doRemoveWinner}
                      />
                    </TabPane>
                  ))}
                </Tabs>
              </div>
            )}
          </Col>
        </Row>

        {/* Print view */}
        {/*<div*/}
        {/*  css={{*/}
        {/*    display: 'none',*/}
        {/*    '@media print': {*/}
        {/*      display: 'block',*/}
        {/*      position: 'static',*/}
        {/*      opacity: 1,*/}
        {/*      zIndex: 'auto',*/}
        {/*    },*/}
        {/*  }}>*/}
        {/*  <TenderOfferPrint*/}
        {/*    projectId={projectId}*/}
        {/*    tenderId={tenderId}*/}
        {/*    isStartFetch*/}
        {/*    isExpandable*/}
        {/*    ref={ref}*/}
        {/*  />*/}
        {/*</div>*/}
      </div>
    );
  }
);

const OffersHeader = ({ offer, isWinner, onSetWinner, onRemoveWinner }) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const menuItems = isWinner
    ? dataContextMenu.offerWinnerContext
    : dataContextMenu.offerNotWinnerContext;

  const menu = (
    <Menu onClick={() => setIsDropdownVisible(false)}>
      {(menuItems || []).map((item) => (
        <Menu.Item
          key={item.modal}
          onClick={() => {
            if (item.modal === 'set-winner') onSetWinner();
            if (item.modal === 'remove-winner') onRemoveWinner();
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icon
              icon={[item.iconStyle as any, item.icon]}
              style={{
                color: item.modal === 'set-winner' ? '#faad14' : '#ff4d4f',
                fontSize: '14px',
              }}
            />
            <span>{intl.formatMessage({ id: item.name })}</span>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div css={styles.panelHeader}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
        {isWinner && (
          <Tooltip title={intl.formatMessage({ id: 'winner-offer' })}>
            <Icon
              icon={['far', 'trophy']}
              style={{ color: '#ffd600', fontSize: 18 }}
            />
          </Tooltip>
        )}
        {!offer.offeror_identity_information ? (
          <div>
            <div>
              {offer.offeror_identity_information?.name ? (
                <span>{offer.offeror_identity_information?.name}</span>
              ) : (
                <span>#{intl.formatMessage({ id: 'no-offer-name' })}</span>
              )}
            </div>
          </div>
        ) : (
          <span>
            {intl.formatMessage(
              { id: 'offers-offer-number' },
              { id: offer.id }
            )}
          </span>
        )}
      </div>

      <div style={{ flex: 1 }}>
        {offer.offeror_identity_information && (
          <div>
            <div>
              {offer.offeror_identity_information?.email ? (
                <span>{offer.offeror_identity_information?.email}</span>
              ) : (
                <span>#{intl.formatMessage({ id: 'no-offer-email' })}</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div css={styles.panelInfo}>
        <div css={styles.panelInfoItem}>
          <span css={styles.panelInfoLabel}>
            {intl.formatMessage({ id: 'vendor' })}:
          </span>
          <span css={styles.panelInfoValue}>
            {offer.created_by_name ||
              intl.formatMessage({ id: 'vendor-name-not-provided' })}
          </span>
        </div>

        <div css={styles.panelInfoItem}>
          <span css={styles.panelInfoLabel}>
            {intl.formatMessage({ id: 'state' })}:
          </span>
          <span css={styles.panelInfoValue}>
            {(() => {
              const { color, text } = getOfferStatusVisualIntl(intl, offer);
              return <span style={{ color }}>{text || '-'}</span>;
            })()}
          </span>
        </div>
      </div>

      <div style={{ marginLeft: 16 }}>
        <Dropdown
          overlay={menu}
          trigger={['click']}
          placement="bottomRight"
          visible={isDropdownVisible}
          onVisibleChange={setIsDropdownVisible}
          getPopupContainer={(trigger) =>
            trigger.parentElement || document.body
          }>
          <IconButton
            icon={['far', 'ellipsis']}
            click={(e) => {
              e.stopPropagation();
              setIsDropdownVisible(!isDropdownVisible);
            }}
            customStyle={{ fontSize: 16 }}
          />
        </Dropdown>
      </div>
    </div>
  );
};

type ContractProcessKey = 'screening' | 'evaluation' | 'result';

const getContractProcessFlags = (state?: number | string) => {
  const rawContractState = Number(state);
  const contractState = Number.isFinite(rawContractState)
    ? rawContractState
    : 0;

  return {
    contractState,
    canOpenEvaluation: contractState >= 6,
    canEditScreening: contractState < 6,
    canEditEvaluation: contractState === 6,
    canOpenResults: contractState > 6,
  };
};

const getDefaultProcessKey = (state?: number | string): ContractProcessKey => {
  const { canOpenResults, canOpenEvaluation } = getContractProcessFlags(state);

  if (canOpenResults) return 'result';
  if (canOpenEvaluation) return 'evaluation';
  return 'screening';
};

type OffersContractProcessContentProps = {
  projectId: number;
  tenderId: number;
  contract: any;
  doSetWinner: (params: any) => any;
  doRemoveWinner: (params: any) => any;
};

const OffersContractProcessContent: FC<OffersContractProcessContentProps> = ({
  projectId,
  tenderId,
  contract,
  doSetWinner,
  doRemoveWinner,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const {
    contractState,
    canOpenEvaluation,
    canEditScreening,
    canEditEvaluation,
    canOpenResults,
  } = getContractProcessFlags(contract.state);

  const [activeProcessKey, setActiveProcessKey] = useState<ContractProcessKey>(
    () => getDefaultProcessKey(contract.state)
  );
  const [isMessagesModalVisible, setIsMessagesModalVisible] = useState(false);

  useEffect(() => {
    setActiveProcessKey(getDefaultProcessKey(contractState));
  }, [contract.id, contractState]);

  const handleProcessTabChange = (key: string) => {
    if (key === 'evaluation' && !canOpenEvaluation) return;
    if (key === 'result' && !canOpenResults) return;

    setActiveProcessKey(key as ContractProcessKey);
  };

  const {
    data: screeningOffersData = [],
    isLoading: isScreeningOffersLoading,
  } = useQuery(
    ['contract-screening-offers', projectId, tenderId, contract.id],
    () =>
      getContractScreeningOffers({
        projectId,
        tenderId,
        contractId: contract.id,
      }),
    {
      enabled:
        !!projectId &&
        !!tenderId &&
        !!contract?.id &&
        activeProcessKey === 'screening',
    }
  );

  const {
    data: evaluationOffersData = [],
    isLoading: isEvaluationOffersLoading,
  } = useQuery(
    ['contract-evaluation-offers', projectId, tenderId, contract.id],
    () =>
      getContractEvaluationOffers({
        projectId,
        tenderId,
        contractId: contract.id,
      }),
    {
      enabled:
        !!projectId &&
        !!tenderId &&
        !!contract?.id &&
        activeProcessKey === 'evaluation' &&
        canOpenEvaluation,
    }
  );

  const {
    data: resultOffersData = [],
    isLoading: isResultOffersLoading,
  } = useQuery(
    ['contract-result-offers', projectId, tenderId, contract?.id],
    () =>
      getContractEvaluationOffers({
        projectId,
        tenderId,
        contractId: contract.id,
      }),
    {
      enabled:
        !!projectId &&
        !!tenderId &&
        !!contract?.id &&
        activeProcessKey === 'result' &&
        !!canOpenResults,
    }
  );

  const evaluationTabLabel = canOpenEvaluation ? (
    intl.formatMessage({ id: 'evaluation-tab' })
  ) : (
    <Tooltip
      title={intl.formatMessage({
        id: 'evaluation-tab-locked-tooltip',
      })}>
      <span>
        {intl.formatMessage({
          id: 'evaluation-tab',
        })}
      </span>
    </Tooltip>
  );

  const isScreeningMessagesButton = activeProcessKey === 'screening';

  const shouldShowEvaluationMessagesButton =
    isScreeningMessagesButton ||
    (activeProcessKey === 'evaluation' && canOpenEvaluation) ||
    (activeProcessKey === 'result' && canOpenResults);

  const evaluationMessagesButton = shouldShowEvaluationMessagesButton ? (
    isScreeningMessagesButton ? (
      <Tooltip
        title={intl.formatMessage({
          id: 'screening-messages-disabled-tooltip',
        })}>
        <span css={styles.disabledMessagesButtonTooltipWrapper}>
          <Button type="primary" css={styles.evaluationMessagesButton} disabled>
            <span css={styles.evaluationMessagesButtonInner}>
              <IconButton icon={['far', 'envelope']} backgroundOff />
              {intl.formatMessage({
                id: 'offers-messages-button',
              })}
            </span>
          </Button>
        </span>
      </Tooltip>
    ) : (
      <Button
        type="primary"
        css={styles.evaluationMessagesButton}
        onClick={() => setIsMessagesModalVisible(true)}>
        <span css={styles.evaluationMessagesButtonInner}>
          <IconButton icon={['far', 'envelope']} backgroundOff />
          {intl.formatMessage({
            id: 'offers-messages-button',
          })}
        </span>
      </Button>
    )
  ) : null;

  return (
    <React.Fragment>
      <div css={styles.tabPaneBody}>
        <div css={styles.tabContentScroll}>
          <div css={styles.contractHeader}>
            <div css={styles.contractHeaderInfo}>
              <div css={styles.contractTitle}>
                #
                {contract.reference
                  ? contract.reference
                  : intl.formatMessage({
                      id: 'no-reference',
                    })}{' '}
                - {contract.name}
              </div>

              <div css={styles.contractDescription}>
                {contract.description ||
                  intl.formatMessage({
                    id: 'contract-description-not-provided',
                  })}
              </div>
            </div>

            {evaluationMessagesButton && (
              <div css={styles.contractHeaderActions}>
                {evaluationMessagesButton}
              </div>
            )}
          </div>

          <div css={styles.processTabsWrap}>
            <Tabs
              activeKey={activeProcessKey}
              onChange={handleProcessTabChange}
              type="card"
              animated={false}
              destroyInactiveTabPane={false}>
              <TabPane
                tab={intl.formatMessage({ id: 'screening-tab' })}
                key="screening">
                {isScreeningOffersLoading ? (
                  <Spin />
                ) : (
                  <OffersScreeningContractView
                    projectId={projectId}
                    tenderId={tenderId}
                    contract={contract}
                    offers={screeningOffersData}
                    isScreeningLocked={!canEditScreening}
                  />
                )}
              </TabPane>

              <TabPane
                tab={evaluationTabLabel}
                key="evaluation"
                disabled={!canOpenEvaluation}>
                {isEvaluationOffersLoading ? (
                  <Spin />
                ) : (
                  <OffersEvaluationContractView
                    projectId={projectId}
                    tenderId={tenderId}
                    contract={contract}
                    offers={evaluationOffersData}
                    isEvaluationLocked={!canEditEvaluation}
                  />
                )}
              </TabPane>

              <TabPane
                tab={
                  <Tooltip
                    title={
                      !canOpenResults
                        ? intl.formatMessage({
                            id: 'offers-results-locked-tooltip',
                          })
                        : undefined
                    }>
                    <span style={{ display: 'block' }}>
                      {intl.formatMessage({
                        id: 'offers-results-tab-label',
                      })}
                    </span>
                  </Tooltip>
                }
                key="result"
                disabled={!canOpenResults}>
                {isResultOffersLoading ? (
                  <Spin />
                ) : (
                  <OffersResultsContractView
                    projectId={projectId}
                    tenderId={tenderId}
                    contract={contract}
                    offers={resultOffersData}
                    doSetWinner={doSetWinner}
                    doRemoveWinner={doRemoveWinner}
                  />
                )}
              </TabPane>
            </Tabs>
          </div>
        </div>
      </div>

      <OffersMessagesModal
        visible={isMessagesModalVisible}
        onCancel={() => setIsMessagesModalVisible(false)}
        projectId={projectId}
        tenderId={tenderId}
        contract={contract}
      />
    </React.Fragment>
  );
};

export const styleFn: ThemedStyleFn = () => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    paddingRight: '88px',
  },

  leftCol: {
    display: 'flex',
    flexDirection: 'column',
  },

  rightCol: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
    overflow: 'hidden',
    paddingTop: 20,
    minHeight: 0,
    width: '100%',
  },

  tabsWrap: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
    '.ant-tabs-nav': { margin: '0 0 8px 0', flexShrink: 0 },
    '.ant-tabs': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      width: '100%',
    },
    '.ant-tabs-content-holder': {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      width: '100%',
    },
    '.ant-tabs-content': {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      width: '100%',
    },
    '.ant-tabs-tabpane': {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      width: '100%',
    },
  },

  tabPaneBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
  },

  tabContentScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '0 12px 12px 0',
    width: '100%',
  },

  processTabsWrap: {
    width: '100%',
    minHeight: 0,

    '.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab': {
      padding: '6px 14px',
      fontSize: 13,
    },

    '.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active': {
      background: '#ffffff',
      borderColor: '#1890ff',
    },
  },

  collapseWrap: {
    minHeight: 0,
    overflow: 'visible',
    width: '100%',
  },

  contractHeader: {
    width: '100%',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    boxSizing: 'border-box',
  },

  contractHeaderInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
    flex: 1,
  },

  contractHeaderActions: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    flexShrink: 0,
    paddingTop: 4,
  },

  disabledMessagesButtonTooltipWrapper: {
    display: 'inline-flex',
  },

  evaluationMessagesButton: {
    '&&': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '168px',
      height: 32,
    },
  },

  evaluationMessagesButtonInner: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    whiteSpace: 'nowrap',
  },

  contractTitle: { fontWeight: 700, fontSize: 18 },

  contractDescription: {
    fontSize: 13,
    color: '#666',
  },

  contractBlock: {
    width: '100%',
    padding: 20,
    // border: '1px solid #ccc',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    // backgroundColor: '#f5f5f5',
    boxSizing: 'border-box',
  },

  contractSubtitle: { fontSize: 12 },

  panelInline: {
    fontWeight: 'bold',
    fontSize: 16,
    borderRadius: 4,
    width: '100%',
  },
  offerBody: { display: 'flex', flexDirection: 'column', width: '100%' },

  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '10px 0',
  },
  panelInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 15,
    flex: 1,
    justifyContent: 'space-between',
  },
  panelInfoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },

  summaryHeaderBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    marginBottom: 20,
    width: '100%',
  },
  summaryHeaderTitle: {
    fontSize: '18px',
    fontWeight: 800,
  },
  summaryHeaderText: {
    color: 'grey',
    whiteSpace: 'pre-wrap',
    width: '100%',
  },

  panelInfoLabel: { color: '#666', fontSize: 13, fontWeight: 'bold' },
  panelInfoValue: { color: '#333', fontSize: 13 },

  rightHeaderHint: { marginBottom: 8, color: '#666', fontSize: 12 },
});
